import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { toOrgStartISO, toOrgEndISO } from "../lib/timezone";
import type { DateRange } from "./useGlobalDateRange";

// =============================================================================
// useShowRateBreakdown — lazy fetch for the Show-up card breakdown modal (HP-A)
// =============================================================================
// Calls get_show_rate_breakdown with params derived EXACTLY like
// useOperationsAnalytics derives its get_operations_kpis params
// (toOrgStartISO/toOrgEndISO on the same dateRange + org tz). That parameter
// parity is what makes the modal's bucket counts tie the card's own terms —
// the RPC's contract is proven against get_operations_kpis on identical params.
// Fetches only while `enabled` (modal open). No cache: the predicate costs
// ~21ms per 90-day window and a stale drilldown under a fresh card is worse
// than the refetch.
// =============================================================================

export interface BreakdownRow {
  lead_id: string | null;
  lead_name: string | null;
  rep_id: string | null;
  rep_name: string | null;
  status: string;
  date: string; // org-tz calendar date, YYYY-MM-DD
  cancel_by: "Host" | "Invitee" | null;
}

export interface ShowRateBreakdown {
  shows_count: number;
  no_shows_count: number;
  orphans_count: number;
  shows: BreakdownRow[];
  no_shows: BreakdownRow[];
  orphans: BreakdownRow[];
}

export function useShowRateBreakdown(
  organizationId: string | undefined,
  dateRange: DateRange,
  orgTz: string | null | undefined,
  enabled: boolean
) {
  const [data, setData] = useState<ShowRateBreakdown | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !organizationId || !dateRange.start || !dateRange.end) return;
    let cancelled = false;

    const fetchBreakdown = async () => {
      if (!supabase) {
        setError("Supabase client not configured");
        return;
      }
      setLoading(true);
      setError(null);
      const { data: payload, error: rpcError } = await supabase.rpc("get_show_rate_breakdown", {
        p_organization_id: organizationId,
        p_start_date: toOrgStartISO(dateRange.start!, orgTz),
        p_end_date: toOrgEndISO(dateRange.end!, orgTz),
      });
      if (cancelled) return;
      if (rpcError) {
        setError(rpcError.message);
        setData(null);
      } else {
        setData(payload as ShowRateBreakdown);
      }
      setLoading(false);
    };

    fetchBreakdown();
    return () => {
      cancelled = true;
    };
  }, [enabled, organizationId, dateRange.start, dateRange.end, orgTz]);

  return { data, loading, error };
}
